import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Send, X, Lightbulb } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Avatar, Help, wordCount } from './shared';

const FIELDS = [
  { k: 'situation', label: 'Situation — wann und wo?', color: 'text-sky-400',
    ph: 'Im Verkäufergespräch mit [Firma] am Dienstag …',
    help: 'Je konkreter der Moment, desto nützlicher das Feedback.' },
  { k: 'behavior', label: 'Verhalten — was hast du beobachtet?', color: 'text-violet-400',
    ph: 'Du hast dreimal unterbrochen, bevor der Verkäufer fertig war …',
    help: 'Beschreiben, nicht interpretieren. ‚Du hast X getan‘ statt ‚Du warst ungeduldig‘. Ein Verhalten kann man ändern — eine Charakterdiagnose nicht.' },
  { k: 'impact', label: 'Wirkung — was hat es ausgelöst?', color: 'text-amber-400',
    ph: 'Bei mir kam an, dass … / Der Verkäufer wirkte daraufhin …',
    help: 'Deine Wahrnehmung ist unbestreitbar wahr — bleib bei ihr. ‚Auf mich wirkte es …‘ statt ‚Alle fanden es …‘.' },
  { k: 'feedforward', label: 'Ein Vorschlag für nächstes Mal? (optional)', color: 'text-emerald-400',
    ph: 'Was könnte er/sie beim nächsten Mal ausprobieren?', help: null },
];

export default function FeedbackForm({ members, meId, prefill, clearPrefill, onSent }) {
  const [toUser, setToUser] = useState(prefill?.kind === 'goal' ? prefill.goal.owner_id : '');
  const [form, setForm] = useState({ situation: '', behavior: '', impact: '', feedforward: '' });
  const [goals, setGoals] = useState([]);
  const [attachGoal, setAttachGoal] = useState(prefill?.kind === 'goal' ? prefill.goal.id : '');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const others = members.filter((m) => m.id !== meId);

  useEffect(() => {
    api.teamDevGoals().then((res) => setGoals(res.data.active || [])).catch(() => {});
  }, []);

  const isRequest = prefill?.kind === 'request';
  const isGoalObs = prefill?.kind === 'goal';
  const recipientId = isRequest ? prefill.request.from_user_id : toUser;
  const recipient = members.find((m) => m.id === recipientId);
  const rName = isRequest ? prefill.request.from_name : (recipient?.name?.split(' ')[0] || 'die Person');
  const recipientGoals = goals.filter((g) => g.owner_id === recipientId && g.status === 'active');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const shortHint = (k) => {
    const v = form[k];
    return v.trim() && wordCount(v) < 10
      ? `Ziemlich knapp — würde ${rName} daraus verstehen, was genau du meinst?` : null;
  };

  const preSubmit = () => {
    if (!isRequest && !toUser) return toast('Magst du noch auswählen, an wen dein Feedback geht?');
    if (!form.situation.trim() || !form.behavior.trim() || !form.impact.trim())
      return toast('Situation, Verhalten und Wirkung — alle drei zusammen machen dein Feedback wertvoll.');
    setConfirming(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const res = await api.teamDevGiveFeedback({
        to_user_id: toUser, ...form,
        request_id: isRequest ? prefill.request.id : '',
        related_goal_id: attachGoal || '',
      });
      setConfirming(false);
      toast.success(res.data.first
        ? 'Dein erstes Feedback ist raus. Schön, dass du dabei bist. 💛'
        : `Danke — sowas zu schreiben kostet Überwindung, und genau davon lebt das Team. ${rName} bekommt es jetzt privat.`,
        { duration: 6000 });
      setForm({ situation: '', behavior: '', impact: '', feedforward: '' });
      setToUser(''); setAttachGoal('');
      clearPrefill();
      onSent();
    } catch (e) {
      setConfirming(false);
      toast.error(e.response?.data?.detail || 'Senden hat nicht geklappt');
    } finally { setSaving(false); }
  };

  return (
    <div className="td-rise bg-card border border-border rounded-lg p-6 max-w-2xl space-y-5" data-testid="td-give-form">
      {isRequest && (
        <div className="flex items-start gap-2.5 bg-emerald-500/5 border border-emerald-500/25 rounded-md px-3.5 py-3 td-pop" data-testid="td-give-request-banner">
          <Avatar name={prefill.request.from_name} className="mt-0.5" />
          <p className="text-xs text-foreground leading-relaxed">
            <span className="font-medium">{prefill.request.from_name}</span> möchte wissen: „{prefill.request.topic}“ — antworte so konkret wie möglich auf genau diese Frage.
          </p>
          <button onClick={clearPrefill} className="ml-auto text-muted-foreground hover:text-foreground shrink-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {!isRequest && (
        <div>
          <p className="text-xs font-medium text-foreground mb-2">An wen geht dein Feedback?</p>
          <div className="flex flex-wrap gap-2">
            {others.map((m) => (
              <button key={m.id} onClick={() => { setToUser(m.id); if (!isGoalObs) setAttachGoal(''); }} data-testid={`td-recipient-${m.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  toUser === m.id ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-border bg-secondary/30 text-foreground hover:bg-secondary/60'}`}>
                <Avatar name={m.name} className="w-4 h-4 text-[9px]" />
                {m.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {recipientId && recipientGoals.length > 0 && (
        <div className="bg-sky-500/5 border border-sky-500/25 rounded-md px-3.5 py-3 space-y-2 td-pop" data-testid="td-give-goal-hint">
          <p className="text-xs text-foreground leading-relaxed flex items-start gap-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
            <span>{rName} arbeitet gerade an: „{recipientGoals[0].title}“{recipientGoals.length > 1 ? ` (+${recipientGoals.length - 1} weiteres Ziel)` : ''}. Hast du dazu etwas beobachtet? (Feedback andocken hilft doppelt.)</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {recipientGoals.map((g) => (
              <button key={g.id} onClick={() => setAttachGoal((a) => (a === g.id ? '' : g.id))} data-testid={`td-attach-goal-${g.id}`}
                className={`text-[10px] rounded-full border px-2.5 py-1 transition-colors ${attachGoal === g.id ? 'border-sky-500/50 bg-sky-500/15 text-sky-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                {attachGoal === g.id ? '✓ angedockt: ' : 'andocken: '}{g.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {FIELDS.map((f) => (
        <div key={f.k}>
          <p className={`text-[10px] font-mono uppercase tracking-wider mb-1.5 ${f.color}`}>{f.label}</p>
          <Textarea value={form[f.k]} onChange={set(f.k)} placeholder={f.ph} rows={2}
            className="text-sm bg-background rounded-md" data-testid={`td-field-${f.k}`} />
          {shortHint(f.k) && f.k !== 'feedforward' && (
            <p className="text-[11px] text-amber-400/90 mt-1 td-pop" data-testid={`td-short-hint-${f.k}`}>{shortHint(f.k)}</p>
          )}
          {f.help && <Help>{f.help}</Help>}
        </div>
      ))}

      <div>
        <Button onClick={preSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6" data-testid="td-give-submit">
          <Send className="w-4 h-4 mr-1.5" /> Feedback senden
        </Button>
        <p className="text-[10px] text-muted-foreground mt-2.5">Dein Feedback sieht nur die Empfängerin / der Empfänger — niemand sonst.</p>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="td-confirm-dialog">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full td-pop">
            <p className="text-sm text-foreground leading-relaxed">
              Kurzer Check: Würdest du das <span className="font-medium">{rName}</span> genauso ins Gesicht sagen?
            </p>
            <p className="text-xs text-muted-foreground mt-2">Falls nein — kurz umformulieren. Falls ja — raus damit.</p>
            <div className="flex gap-2 mt-5">
              <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex-1" data-testid="td-confirm-send">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : '✅'} Absenden
              </Button>
              <Button variant="outline" onClick={() => setConfirming(false)} className="rounded-full flex-1" data-testid="td-confirm-reread">
                ✏️ Nochmal lesen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
